import { MDXLayout as Layout } from "@/shared/components/MDX/MDXLayout";
import { Alert, Badge, ImageContainer } from "@/shared/components/MDX/MDXComponents";

# 🌐 راهنمای جامع مدیریت سازمان و دسترسی کاربران

پلتفرم کارخانه‌سازی میمزاد (Mimzad Manufacturing Platform) — سند راهنمای کاربری

<Badge type="gold">سند استاندارد کاربری</Badge> <Badge>راهنمای جامع</Badge>

## 📋 فهرست مطالب و دسترسی سریع

1. [مقدمه و مفاهیم درخت سازمان](#sec-intro)
2. [ایجاد سازمان جدید (مادرمجموعه/هلدینگ)](#sec-step1)
3. [تعریف دپارتمان‌ها و واحدهای کاری زیرمجموعه](#sec-step2)
4. [مدیریت اعضا و تخصیص نقش‌های سازمانی](#sec-step3)
5. [مدیریت دسترسی‌های نقطه‌ای کاربر (Override)](#sec-step4)
6. [ریست رمز عبور کاربران توسط مدیر سیستم](#sec-step5)
7. [نکات کاربری و جمع‌بندی امنیتی](#sec-summary)

---

## 1. مقدمه و ساختار چندسطحی سازمان‌ها

در پلتفرم میمزاد، سیستم مدیریت سازمان به شما امکان می‌دهد ساختار پیچیده هلدینگ‌ها، کارخانجات، دپارتمان‌ها، واحدهای تولیدی و ایستگاه‌های کاری را در قالب یک **درخت سلسله‌مراتب سازمانی چندسطحی** تعریف و مدیریت نمایید.

هر سازمان می‌تواند شامل زیرمجموعه‌های فرزند (Child Organizations) و اعضای فعال (Members) با نقش‌ها و دسترسی‌های دقیق باشد.

<ImageContainer src="images/org-image0001.png" alt="نمای کلی سلسله‌مراتب سازمانی در میمزاد">
  📷 تصویر شماره ۱: نمای کلی صفحه مدیریت سلسله‌مراتب سازمانی، درخت سازمان‌ها و اعضای فعال (images/org-image0001.png)
</ImageContainer>

<Alert type="info">
  **ویژگی کلیدی:** ساختار هرم سازمانی در میمزاد از نوع چندمستأجری (Multi-Tenant) بوده و تمامی دادهای هر سازمان به‌صورت کاملاً ایزوله و امن نگهداری می‌شوند.
</Alert>

---

## 2. مرحله اول: ایجاد سازمان جدید (مادرمجموعه / شرکت اصلی)

جهت ثبت یک سازمان جدید در سیستم، مراحل زیر را دنبال کنید:

1. در بالای سمت راست صفحه سلسله‌مراتب سازمانی، روی دکمه **«➕ سازمان جدید»** کلیک کنید.
2. در مدال بازشده، اطلاعات زیر را وارد نمایید:
   - **کد سازمان (یکتا):** شناسه یکتای انگلیسی (مانند `negin-motor`).
   - **نام سازمان:** عنوان رسمی سازمان به فارسی (مانند **نگین موتور**).
   - **نوع سازمان:** انتخاب یکی از گزینه‌های _شرکت (Company)_، _هلدینگ_، _دپارتمان_ یا _واحد کاری_.
3. روی دکمه **«ثبت سازمان»** کلیک کنید تا سازمان جدید ایجاد و در لیست اضافه شود.

<ImageContainer src="images/org-image0002.png" alt="فرم ایجاد سازمان جدید در میمزاد">
  📷 تصویر شماره ۲: فرم ثبت و تعریف سازمان جدید در سیستم (images/org-image0002.png)
</ImageContainer>

<Alert type="warning">
  **توجه:** کد سازمان پس از ثبت قابل تغییر نیست و جهت مراجعات فنی و شناسه‌های سیستم مورد استفاده قرار می‌گیرد.
</Alert>

---

## 3. مرحله دوم: ایجاد دپارتمان‌ها و واحدهای کاری زیرمجموعه

پس از ثبت شرکت اصلی، می‌توانید دپارتمان‌ها و زیرواحدهای کاری را به درخت سازمانی اضافه کنید:

1. دوباره دکمه **«➕ سازمان جدید»** را بزنید و دپارتمان‌های موردنظر (مانند **دپارتمان تولید** و **دپارتمان کیفیت**) را با نوع _دپارتمان_ ثبت نمایید.
2. سازمان اصلی (مانند نگین موتور) را از درخت انتخاب کرده و در بخش **«افزودن سازمان فرزند (زیرمجموعه)»**، دپارتمان‌های ثبت‌شده را انتخاب و روی **«افزودن به‌عنوان زیرمجموعه»** کلیک کنید.
3. برای ایجاد زیرواحدها (مانند **واحد مونتاژ نهایی**)، همین مراحل را برای دپارتمان مربوطه تکرار کنید تا درخت چندسطحی شکل گیرد.

<ImageContainer src="images/org-image0003.png" alt="نمایش درخت ۳ سطحی سازمان">
  📷 تصویر شماره ۳: ساختار درخت سازمانی ۳ سطحی (نگین موتور > دپارتمان‌ها > واحدها) (images/org-image0003.png)
</ImageContainer>

---

## 4. مرحله سوم: مدیریت اعضا و تخصیص نقش‌های سازمانی

برای افزودن پرسنل و اعضا به هر سازمان یا دپارتمان:

1. سازمان یا واحد موردنظر را از درخت سازمانی انتخاب کنید.
2. در تب **«اعضا»**، دکمه **«➕ کاربر جدید»** را کلیک کنید.
3. نام، نام خانوادگی، نام کاربری، ایمیل و رمز عبور اولیه را وارد کرده و دکمه **«ثبت کاربر»** را بزنید.
4. کاربر ایجادشده را از منوی کشویی اعضا انتخاب، نقش سازمانی (_عضو_، _مدیر_ یا _مدیر ارشد_) را تعیین و روی دکمه **«افزودن»** کلیک کنید.

<ImageContainer src="images/org-image0004.png" alt="فرم ثبت کاربر جدید و مدیریت اعضا">
  📷 تصویر شماره ۴: ثبت کاربر جدید در مستأجر و مدیریت اعضای فعال سازمان (images/org-image0004.png)
</ImageContainer>

### کاتالوگ نقش‌های سازمانی

| عنوان نقش | کد سیستم | سطح دسترسی و توضیحات |
| :--- | :--- | :--- |
| **عضو (Member)** | `member` | دسترسی پایه جهت مشاهده اطلاعات و عملیات استاندارد در سطح سازمان. |
| **مدیر (Manager)** | `manager` | دسترسی مدیریتی جهت نظارت بر خطوط تولید، انبار و گزارش‌گیری واحد. |
| **مدیر ارشد (Administrator)** | `administrator` | دسترسی کامل مدیریتی به تمام بخش‌ها، اعضا و تنظیمات سازمان. |

---

## 5. مرحله چهارم: مدیریت دسترسی‌های نقطه‌ای کاربر (Override)

در پلتفرم میمزاد علاوه بر نقش‌های کلی، می‌توانید برای یک کاربر خاص مجوزهای مشخصی را به‌صورت مستقیماً **اعطا (Grant)** یا **سلب (Deny)** نمایید:

1. در لیست اعضای فعال سازمان، روی دکمه **«⚙️ مجوزها»** در ردیف کاربر موردنظر کلیک کنید.
2. مدال مدیریت دسترسی‌های کاربر باز می‌شود که تمام مجوزهای سیستم (انبار، تولید، کیفیت، گزارش‌گیری و ...) را نمایش می‌دهد.
3. برای هر مجوز می‌توانید یکی از ۳ وضعیت زیر را انتخاب کنید:
   - **پیش‌فرض (از نقش):** دسترسی بر اساس نقش کاربر تعیین می‌شود.
   - **فعال (Grant):** دسترسی مستقیماً به کاربر اعطا می‌شود (حتی اگر در نقش او نباشد).
   - **غیرفعال (Deny):** دسترسی صریحاً از کاربر سلب می‌شود (حتی اگر نقش او این دسترسی را داشته باشد).
4. در نهایت روی دکمه **«ذخیره تغییرات»** کلیک کنید.

<ImageContainer src="images/org-image0005.png" alt="مدال مدیریت دسترسی‌های کاربر">
  📷 تصویر شماره ۵: مدال مدیریت دسترسی‌های نقطه‌ای کاربر (Per-User Permission Overrides) (images/org-image0005.png)
</ImageContainer>

<Alert type="tip">
  **اولویت ارزیابی دسترسی‌ها:** سلب مستقیم (Deny) ➔ اعطای مستقیم (Grant) ➔ نقش‌های کاربر (Role).
</Alert>

---

## 6. مرحله پنجم: ریست رمز عبور کاربر توسط مدیر سیستم

در صورتی که کاربری رمز عبور خود را فراموش کند یا نیاز به ریست ایمن داشته باشد:

1. در لیست اعضای فعال، روی دکمه **«🔑 ریست رمز»** کلیک کنید.
2. دیالوگ تایید نمایش داده می‌شود. پس از تایید، سیستم یک **رمز عبور جدید موقت و ایمن** تولید می‌کند.
3. رمز عبور فقط یک‌بار روی صفحه به شما نمایش داده می‌شود؛ آن را کپی کرده و به کاربر تحویل دهید.
4. کاربر در اولین ورود بعدی ملزم به تغییر این رمز عبور موقت خواهد بود (امپراتیو `must_change_password=True`).

<ImageContainer src="images/org-image0006.png" alt="دیالوگ ریست رمز عبور توسط مدیر">
  📷 تصویر شماره ۶: دیالوگ ریست رمز عبور ایمن توسط مدیر سیستم (images/org-image0006.png)
</ImageContainer>

---

## 7. نکات کاربردی و جمع‌بندی امنیتی

- همواره کدهای شناسه سازمان‌ها و کدهای مجوز را استاندارد و منظم انتخاب کنید.
- حداقل یک کاربر با نقش **مدیر ارشد (Administrator)** برای هر سازمان اصلی تعریف نمایید.
- جهت حفظ امنیت، رمزهای عبور اولیه موقت را از طریق کانال‌های ارتباطی امن به پرسنل اطلاع دهید.
- در صورتی که مایل به لغو عضویت یک کاربر در سازمان هستید، کافی است دکمه **«حذف»** را در ردیف مربوطه کلیک کنید.


export default function Page({ children }) {
  return <Layout>{children}</Layout>;
}
